import React, { useEffect, useState, useMemo } from 'react';
import { fetchEditCo2Weight, updateCo2Weight, deleteCo2Weight, fetchCostCategories, fetchNewCostQuantityTypes, fetchRegions } from '../services/api';
import { useRegion } from '../context/RegionContext';
import './PageLayout.css';
import './EditCo2WeightPage.css';

// ✅ SUCCESS MODAL
const SuccessModal = ({ isOpen, onClose, title, message, icon }) => {
  if (!isOpen) return null;

  return (
    <div className="success-modal-overlay" onClick={onClose}>
      <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="success-modal-header">
          <div className="success-modal-icon">
            {icon || '✅'}
          </div>
          <h2>{title || 'Success!'}</h2>
        </div>
        <div className="success-modal-body">
          <p>{message}</p>
        </div>
        <div className="success-modal-footer">
          <button className="success-modal-button" onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// ✅ ERROR MODAL
const ErrorModal = ({ isOpen, onClose, title, message, icon }) => {
  if (!isOpen) return null;

  return (
    <div className="success-modal-overlay" onClick={onClose}>
      <div className="success-modal-content error-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="success-modal-header error-modal-header">
          <div className="success-modal-icon error-modal-icon">
            {icon || '⚠️'}
          </div>
          <h2>{title || 'Error'}</h2>
        </div>
        <div className="success-modal-body">
          <p style={{ whiteSpace: 'pre-line' }}>{message}</p>
        </div>
        <div className="success-modal-footer">
          <button className="success-modal-button error-modal-button" onClick={onClose}>
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// ✅ NEW: CONFIRM MODAL (matches your screenshot styling)
const ConfirmModal = ({ isOpen, onClose, onConfirm, title, message, confirmText, cancelText, icon }) => {
  if (!isOpen) return null;

  return (
    <div className="success-modal-overlay" onClick={onClose}>
      <div className="success-modal-content confirm-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="success-modal-header confirm-modal-header">
          <div className="success-modal-icon confirm-modal-icon">
            {icon || '⚠️'}
          </div>
          <h2>{title || 'Confirm Action'}</h2>
        </div>
        <div className="success-modal-body">
          <p style={{ whiteSpace: 'pre-line' }}>{message}</p>
        </div>
        <div className="success-modal-footer confirm-modal-footer">
          <button 
            className="success-modal-button confirm-button" 
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {confirmText || 'OK'}
          </button>
          <button 
            className="success-modal-button cancel-button" 
            onClick={onClose}
          >
            {cancelText || 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ✅ EDIT MATERIAL MODAL — themed, friendly editor for a single row
const EditMaterialModal = ({
    isOpen, onClose, onSave, editForm, onChange,
    epdCategories, quantityTypes, regionName,
}) => {
    if (!isOpen) return null;

    return (
        <div className="ec2-overlay" onClick={onClose}>
            <div className="ec2-modal" onClick={(e) => e.stopPropagation()}>
                <div className="ec2-modal__header">
                    <div>
                        <h2 className="ec2-modal__title">✏️ Edit Material</h2>
                        <p className="ec2-modal__subtitle">{regionName} · {editForm.MeasurementUnitRateId}</p>
                    </div>
                    <button className="ec2-modal__close" onClick={onClose} aria-label="Close">×</button>
                </div>

                <div className="ec2-modal__body">
                    <div className="ec2-grid">
                        <div className="ec2-field ec2-field--full">
                            <label className="ec2-label">Name</label>
                            <input
                                className="ec2-input"
                                type="text"
                                name="MeasurementUnitName"
                                value={editForm.MeasurementUnitName}
                                onChange={onChange}
                                placeholder="Material name"
                            />
                        </div>

                        <div className="ec2-field">
                            <label className="ec2-label">Category</label>
                            <select
                                className="ec2-select"
                                name="Category"
                                value={editForm.Category}
                                onChange={onChange}
                            >
                                <option value="">Select Category</option>
                                {epdCategories.map((cat) => (
                                    <option key={cat.Id} value={cat.Id}>{cat.Categories}</option>
                                ))}
                            </select>
                        </div>

                        <div className="ec2-field">
                            <label className="ec2-label">Quantity Type</label>
                            <select
                                className="ec2-select"
                                name="QuantityTypeName"
                                value={editForm.QuantityTypeName}
                                onChange={onChange}
                            >
                                <option value="">Select Quantity Type</option>
                                {Array.isArray(quantityTypes) && quantityTypes.map((qt) => (
                                    <option key={qt.Id} value={qt.Id}>{qt.Name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="ec2-field">
                            <label className="ec2-label">CO2</label>
                            <input
                                className="ec2-input"
                                type="number"
                                name="Co2Value"
                                value={editForm.Co2Value}
                                onChange={onChange}
                                placeholder="0.00000"
                            />
                        </div>

                        <div className="ec2-field">
                            <label className="ec2-label">Biogenic</label>
                            <input
                                className="ec2-input"
                                type="number"
                                name="BiogenicValue"
                                value={editForm.BiogenicValue}
                                onChange={onChange}
                                placeholder="0.00000"
                            />
                        </div>

                        <div className="ec2-field">
                            <label className="ec2-label">Weight</label>
                            <input
                                className="ec2-input"
                                type="number"
                                name="WeightValue"
                                value={editForm.WeightValue}
                                onChange={onChange}
                                placeholder="0.00000"
                            />
                        </div>

                        <div className="ec2-field">
                            <label className="ec2-label">External Ref Type</label>
                            <input
                                className="ec2-input"
                                type="text"
                                name="ExternalRefType"
                                value={editForm.ExternalRefType}
                                onChange={onChange}
                                placeholder="e.g. NABERS"
                                maxLength="45"
                            />
                        </div>

                        <div className="ec2-field ec2-field--full">
                            <label className="ec2-label">External Ref ID</label>
                            <input
                                className="ec2-input"
                                type="text"
                                name="ExternalRefId"
                                value={editForm.ExternalRefId}
                                onChange={onChange}
                                placeholder="External reference id"
                                maxLength="255"
                            />
                        </div>
                    </div>
                </div>

                <div className="ec2-modal__footer">
                    <button className="ec2-modal__btn ec2-modal__btn--cancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button className="ec2-modal__btn ec2-modal__btn--save" onClick={onSave}>
                        💾 Save Changes
                    </button>
                </div>
            </div>
        </div>
    );
};

function EditCo2WeightPage() {
    const { selectedRegion } = useRegion();
    
    const [materials, setMaterials] = useState([]);
    const [filters, setFilters] = useState({});
    const [categories, setCategories] = useState([]);
    const [quantityTypes, setQuantityTypes] = useState([]);
    const [availableRegions, setAvailableRegions] = useState([]);
    const [editIndex, setEditIndex] = useState(null);
    
    const [loading, setLoading] = useState(false);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [loadingQuantityTypes, setLoadingQuantityTypes] = useState(false);

    // ✅ NEW: PAGINATION STATE
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50); // Default 50 items per page

    // ✅ SUCCESS MODAL STATE
    const [successModal, setSuccessModal] = useState({
        open: false,
        title: '',
        message: '',
        icon: '✅',
    });

    // ✅ ERROR MODAL STATE
    const [errorModal, setErrorModal] = useState({
        open: false,
        title: '',
        message: '',
        icon: '⚠️',
    });

    // ✅ NEW: CONFIRM MODAL STATE
    const [confirmModal, setConfirmModal] = useState({
        open: false,
        title: '',
        message: '',
        icon: '⚠️',
        onConfirm: null,
        itemId: null,
    });

    // ✅ HELPER FUNCTIONS FOR MODALS
    const showSuccessModal = (title, message, icon = '✅') => {
        setSuccessModal({ open: true, title, message, icon });
    };

    const showErrorModal = (title, message, icon = '⚠️') => {
        setErrorModal({ open: true, title, message, icon });
    };

    // ✅ NEW: HELPER FOR CONFIRM MODAL
    const showConfirmModal = (title, message, onConfirm, itemId, icon = '⚠️') => {
        setConfirmModal({ open: true, title, message, onConfirm, itemId, icon });
    };

    const [editForm, setEditForm] = useState({
        MeasurementUnitRateId: '',
        MeasurementUnitName: '',
        Co2Value: '',
        BiogenicValue: '',
        WeightValue: '',
        Category: '',
        QuantityTypeName: '',
        ExternalRefType: '',   
        ExternalRefId: '' 
    });

    useEffect(() => {
        (async () => {
            try {
                const regions = await fetchRegions();
                console.log('🔍 EDIT CO2: Fetched regions:', regions);
                setAvailableRegions(Array.isArray(regions) ? regions : []);
            } catch (e) {
                console.error('Error loading regions:', e);
                setAvailableRegions([]);
            }
        })();
    }, []);

    const regionName = useMemo(() => {
        if (!Array.isArray(availableRegions) || availableRegions.length === 0) {
            return selectedRegion;
        }
        const r = availableRegions.find((r) => r.id === selectedRegion);
        return r ? r.name : selectedRegion;
    }, [availableRegions, selectedRegion]);

    useEffect(() => {
        if (!selectedRegion) {
            console.log('🔍 EDIT CO2: No selectedRegion yet');
            return;
        }

        const loadCategories = async () => {
            setLoadingCategories(true);
            try {
                console.log('🔍 EDIT CO2: Loading categories for region:', selectedRegion);
                const categoriesData = await fetchCostCategories(selectedRegion);
                console.log("📌 Categories for region", selectedRegion, ":", categoriesData);
                
                if (Array.isArray(categoriesData)) {
                    setCategories(categoriesData);
                } else if (categoriesData && Array.isArray(categoriesData.data)) {
                    setCategories(categoriesData.data);
                } else {
                    console.warn('Categories data is not an array:', categoriesData);
                    setCategories([]);
                }
            } catch (error) {
                console.error('❌ Error fetching categories:', error);
                setCategories([]);
            } finally {
                setLoadingCategories(false);
            }
        };

        const loadQuantityTypes = async () => {
            setLoadingQuantityTypes(true);
            try {
                console.log('🔍 EDIT CO2: Loading quantity types for region:', selectedRegion);
                const quantityTypesData = await fetchNewCostQuantityTypes(selectedRegion);
                console.log("📌 Quantity Types for region", selectedRegion, ":", quantityTypesData);
                
                if (Array.isArray(quantityTypesData)) {
                    setQuantityTypes(quantityTypesData);
                } else if (quantityTypesData && Array.isArray(quantityTypesData.data)) {
                    setQuantityTypes(quantityTypesData.data);
                } else {
                    console.warn('Quantity types data is not an array:', quantityTypesData);
                    setQuantityTypes([]);
                }
            } catch (error) {
                console.error('❌ Error fetching quantity types:', error);
                setQuantityTypes([]);
            } finally {
                setLoadingQuantityTypes(false);
            }
        };

       const loadCo2Data = async () => {
            if (!selectedRegion) return;
            
            setLoading(true);
            try {
                console.log('🔍 LOADING CO2 DATA - Region:', selectedRegion);
                const response = await fetchEditCo2Weight(selectedRegion);
                console.log('🔍 LOADING CO2 DATA - Raw response:', response);
                
                if (response && Array.isArray(response.data)) {
                    console.log('🔍 LOADING CO2 DATA - Setting materials:', response.data);
                    setMaterials(response.data);
                } else if (Array.isArray(response)) {
                    console.log('🔍 LOADING CO2 DATA - Setting materials (array):', response);
                    setMaterials(response);
                } else {
                    console.warn('🔍 LOADING CO2 DATA - No valid data:', response);
                    setMaterials([]);
                }
            } catch (error) {
                console.error('Error fetching CO2 data:', error);
                showErrorModal(
                    'Failed to Load Data',
                    `Could not load CO2 weight data for ${regionName}.\n\nError: ${error?.message || String(error)}`
                );
                setMaterials([]);
            } finally {
                setLoading(false);
            }
        };

        loadCategories();
        loadQuantityTypes();
        loadCo2Data();
    }, [selectedRegion, regionName]);

    const loadCo2Data = async () => {
        if (!selectedRegion) return;
        
        setLoading(true);
        try {
            const response = await fetchEditCo2Weight(selectedRegion);
            if (response && Array.isArray(response.data)) {
                setMaterials(response.data);
            } else if (Array.isArray(response)) {
                setMaterials(response);
            } else {
                setMaterials([]);
            }
        } catch (error) {
            console.error('Error fetching CO2 data:', error);
            showErrorModal(
                'Failed to Refresh Data',
                `Could not refresh CO2 weight data.\n\nError: ${error?.message || String(error)}`
            );
            setMaterials([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters({
            ...filters,
            [name]: value === 'All' ? undefined : value,
        });
        // ✅ Reset to page 1 when filters change
        setCurrentPage(1);
    };

    // Add this after the categories state is loaded
    // ✅ Filter categories to only show those starting with "EPD"
    const epdCategories = useMemo(() => {
        return Array.isArray(categories) 
            ? categories.filter(cat => cat.Categories?.startsWith('EPD'))
            : [];
    }, [categories]);


    const handleEdit = (id) => {
        if (!id) {
            console.error("❌ Invalid ID for editing:", id);
            return;
        }

        console.log("🔍 Editing item with ID:", id);

        const selectedMaterial = materials.find(mat => mat.MeasurementUnitRateId === id);
        if (!selectedMaterial) {
            console.error("❌ Material not found for ID:", id);
            return;
        }

        console.log("✅ Selected Material:", selectedMaterial);

        const matchingCategory = categories.find(cat => cat.Id === selectedMaterial.CategoryId);
        const matchingQuantityType = quantityTypes.find(qt => qt.Id === selectedMaterial.QuantityTypeId);

        console.log("🔍 Category lookup:", { 
            selectedCategoryId: selectedMaterial.CategoryId, 
            matchingCategory,
            availableCategories: categories.length 
        });
        
        console.log("🔍 QuantityType lookup:", { 
            selectedQuantityTypeId: selectedMaterial.QuantityTypeId, 
            matchingQuantityType,
            availableQuantityTypes: quantityTypes.length 
        });

        const newEditForm = {
            MeasurementUnitRateId: selectedMaterial.MeasurementUnitRateId,
            MeasurementUnitName: selectedMaterial.MeasurementUnitName || "",
            Co2Value: selectedMaterial.Co2Value !== undefined ? selectedMaterial.Co2Value : "",
            BiogenicValue: selectedMaterial.BiogenicValue !== undefined ? selectedMaterial.BiogenicValue : "",
            WeightValue: selectedMaterial.WeightValue !== undefined ? selectedMaterial.WeightValue : "",
            Category: matchingCategory ? matchingCategory.Id : (selectedMaterial.CategoryId || ''),
            QuantityTypeName: matchingQuantityType ? matchingQuantityType.Id : (selectedMaterial.QuantityTypeId || ''),
            ExternalRefType: selectedMaterial.ExternalRefType || "",     
            ExternalRefId: selectedMaterial.ExternalRefId || "" 
        };

        setEditIndex(id);
        setEditForm(newEditForm);

        console.log("✅ Updated Edit Form Data:", newEditForm);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditForm({ ...editForm, [name]: value });
    };

    const handleSave = async () => {
        if (!editForm.MeasurementUnitRateId) {
            console.error("❌ No valid ID found for update:", editForm);
            showErrorModal('Invalid Data', 'No valid ID found for update. Please try again.');
            return;
        }

        console.log("🔍 SAVE - Current editForm:", editForm);

        const processValue = (value) => {
            if (value === '' || value === null || value === undefined) return null;
            if (typeof value === 'string' && value.trim() === '') return null;
            return value;
        };

        const processNumberValue = (value) => {
            if (value === '' || value === null || value === undefined) return null;
            const num = parseFloat(value);
            return isNaN(num) ? null : num;
        };

        const updatedData = {
            id: editForm.MeasurementUnitRateId,
            name: processValue(editForm.MeasurementUnitName),
            co2Value: processNumberValue(editForm.Co2Value),
            biogenicValue: processNumberValue(editForm.BiogenicValue),
            weightValue: processNumberValue(editForm.WeightValue),
            categoryId: processValue(editForm.Category),
            quantityTypeId: processValue(editForm.QuantityTypeName),
            externalRefType: processValue(editForm.ExternalRefType),  
            externalRefId: processValue(editForm.ExternalRefId) 
        };

        console.log("📤 SAVE - Sending update data:", updatedData);

        try {
            const response = await updateCo2Weight(editForm.MeasurementUnitRateId, updatedData, selectedRegion);
            console.log("✅ SAVE - Update Response:", response);
            
            setEditIndex(null);
            await loadCo2Data();
            
            showSuccessModal(
                'Update Successful',
                `CO2 weight data updated successfully for ${regionName}!\n\nID: ${editForm.MeasurementUnitRateId}`,
                '✅'
            );
        } catch (error) {
            console.error("❌ Error updating CO2 data:", error?.response?.data || error);
            
            const errorMessage = error?.response?.data?.error || error?.message || 'Unknown error occurred';
            const errorDetails = error?.response?.data?.debug ? 
                `\n\nDebug Info:\n${JSON.stringify(error.response.data.debug, null, 2)}` : '';
            
            showErrorModal(
                'Update Failed',
                `Failed to update CO2 weight data for ${regionName}.\n\nError: ${errorMessage}${errorDetails}`,
                '❌'
            );
        }
    };

    // ✅ UPDATED: Show confirm modal instead of window.confirm
    const handleDelete = (id) => {
        showConfirmModal(
            'Ops Console',  // Matches your screenshot title
            `Are you sure you want to delete this CO2 data?\n\nID: ${id}\n\nThis action cannot be undone.`,
            () => performDelete(id),  // Pass the actual delete function
            id,
            '⚠️'
        );
    };

    // ✅ NEW: Actual delete logic (extracted from handleDelete)
    const performDelete = async (id) => {
        try {
            setLoading(true);
            console.log('🗑️ Deleting CO2 data for ID:', id);
            
            const response = await deleteCo2Weight(id, selectedRegion);
            console.log('✅ Delete response:', response);
            
            await loadCo2Data();
            
            showSuccessModal(
                'Deletion Successful',
                `CO2 weight data deleted successfully for ${regionName}!\n\nDeleted ID: ${id}`,
                '🗑️'
            );
        } catch (error) {
            console.error("❌ Error deleting CO2 data:", error);
            
            const errorMessage = error?.response?.data?.error || error?.message || 'Unknown error occurred';
            const statusCode = error?.response?.status;
            
            let userFriendlyMessage = `Failed to delete CO2 weight data.\n\nError: ${errorMessage}`;
            
            if (statusCode === 403) {
                userFriendlyMessage = `Access Denied\n\nYou don't have permission to delete CO2 weight data.\n\nThis operation requires admin access.`;
            } else if (statusCode === 404) {
                userFriendlyMessage = `Not Found\n\nThe CO2 weight data with ID "${id}" could not be found in ${regionName}.\n\nIt may have already been deleted.`;
            }
            
            showErrorModal(
                'Deletion Failed',
                userFriendlyMessage,
                '❌'
            );
        } finally {
            setLoading(false);
        }
    };

    // ✅ FILTERING: Apply filters to ALL materials (across all pages)
    const filteredMaterials = useMemo(() => {
        return Array.isArray(materials) ? materials.filter((m) => (
            (filters.MeasurementUnitRateId === undefined || m.MeasurementUnitRateId?.toString().includes(filters.MeasurementUnitRateId)) &&
            (filters.MeasurementUnitName === undefined || m.MeasurementUnitName?.toLowerCase().includes((filters.MeasurementUnitName || '').toLowerCase())) &&
            (filters.Category === undefined || 
            (filters.Category === 'NO_CATEGORY' 
            ? (!m.CategoryId || m.CategoryId === null || m.CategoryId === '') 
            : (m.CategoryId === filters.Category))) &&
            (filters.QuantityTypeName === undefined || m.QuantityTypeId === filters.QuantityTypeName)
        )) : [];
    }, [materials, filters]);

    // ✅ PAGINATION CALCULATIONS
    const totalPages = Math.ceil(filteredMaterials.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentPageMaterials = filteredMaterials.slice(startIndex, endIndex);

    // ✅ PAGINATION CONTROLS
    const goToPage = (pageNumber) => {
        setCurrentPage(Math.max(1, Math.min(pageNumber, totalPages)));
    };

    const handleItemsPerPageChange = (e) => {
        const newItemsPerPage = parseInt(e.target.value);
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1); // Reset to first page
    };

    // ✅ GENERATE PAGE NUMBERS FOR PAGINATION
    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 7;
        
        if (totalPages <= maxVisiblePages) {
            // Show all pages if total is small
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Smart pagination with ellipsis
            if (currentPage <= 4) {
                // Near start
                for (let i = 1; i <= 5; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 3) {
                // Near end
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
            } else {
                // Middle
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            }
        }
        
        return pages;
    };

    console.log('🔍 EDIT CO2 RENDER STATE:', {
        selectedRegion,
        materialsCount: materials?.length || 0,
        categoriesCount: categories?.length || 0,
        quantityTypesCount: quantityTypes?.length || 0,
        loading,
        filteredCount: filteredMaterials.length,
        currentPage,
        totalPages
    });

    return (
        <div>
            <div className="page-title-section">
                <h1>Edit CO2, Biogenic, and Weight Data</h1>
                <div className="region-indicator-under-title">
                    🌏 Current Region: <strong>{regionName}</strong>
                </div>
            </div>

            <div className="supply-rates-container">
                <div className="filter-section">
                    <h3>Filters</h3>
                    
                    <div className="filter-group">
                        <label htmlFor="MeasurementUnitRateId">ID</label>
                        <input
                            type="text"
                            name="MeasurementUnitRateId"
                            value={filters.MeasurementUnitRateId || ''}
                            onChange={handleFilterChange}
                            placeholder="Filter by ID"
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="MeasurementUnitName">Name</label>
                        <input
                            type="text"
                            name="MeasurementUnitName"
                            value={filters.MeasurementUnitName || ''}
                            onChange={handleFilterChange}
                            placeholder="Filter by Name"
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="Category">Category</label>
                        <select
                            name="Category"
                            value={filters.Category || 'All'}
                            onChange={handleFilterChange}
                        >
                            <option value="All">All</option>
                            {/* ✅ NEW: Add "No Category" option */}
                            <option value="NO_CATEGORY" style={{ fontStyle: 'italic', color: '#666' }}>
                                (No Category)
                            </option>
                            {loadingCategories ? (
                                <option disabled>Loading categories...</option>
                            ) : (
                                epdCategories.length > 0 ? (
                                    epdCategories.map((category) => (
                                        <option key={category.Id} value={category.Id}>
                                            {category.Categories}
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>No EPD categories available</option>
                                )
                            )}
                        </select>
                    </div>


                    <div className="filter-group">
                        <label htmlFor="QuantityTypeName">Quantity Type</label>
                        <select
                            name="QuantityTypeName"
                            value={filters.QuantityTypeName || 'All'}
                            onChange={handleFilterChange}
                        >
                            <option value="All">All</option>
                            {loadingQuantityTypes ? (
                                <option disabled>Loading quantity types...</option>
                            ) : (
                                Array.isArray(quantityTypes) && quantityTypes.length > 0 ? (
                                    quantityTypes.map((qt) => (
                                        <option key={qt.Id} value={qt.Id}>
                                            {qt.Name}
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>No quantity types available</option>
                                )
                            )}
                        </select>
                    </div>

                    {/* ✅ NEW: ITEMS PER PAGE SELECTOR */}
                    <div className="filter-group">
                        <label htmlFor="itemsPerPage">Items Per Page</label>
                        <select
                            name="itemsPerPage"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                        >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="200">200</option>
                            <option value="500">500</option>
                        </select>
                    </div>
                </div>

                <div className="table-section">
                    {/* ✅ NEW: PAGINATION INFO AND CONTROLS (TOP) */}
                    {!loading && filteredMaterials.length > 0 && (
                        <div className="pagination-info-top" style={{ 
                            marginBottom: '15px', 
                            padding: '12px 15px', 
                            background: '#f8f9fa', 
                            borderRadius: '6px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '10px'
                        }}>
                            <div style={{ fontSize: '14px', color: '#555' }}>
                                Showing <strong>{startIndex + 1}</strong> to <strong>{Math.min(endIndex, filteredMaterials.length)}</strong> of <strong>{filteredMaterials.length}</strong> materials
                                {materials.length !== filteredMaterials.length && (
                                    <span style={{ color: '#007bff', marginLeft: '8px' }}>
                                        (filtered from {materials.length} total)
                                    </span>
                                )}
                            </div>
                            <div style={{ fontSize: '14px', color: '#555' }}>
                                Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <p>Loading CO2 data for {regionName}...</p>
                    ) : filteredMaterials.length === 0 ? (
                        <p>No CO2 data found for {regionName}...</p>
                    ) : (
                        <>
                            <table className="supply-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Category</th>
                                        <th>Name</th>
                                        <th>Quantity Type</th>
                                        <th>CO2</th>
                                        <th>Biogenic</th>
                                        <th>Weight</th>
                                        <th>External Ref Type</th> 
                                        <th>External Ref ID</th> 
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentPageMaterials.map((rate, index) => (
                                        <tr key={rate.MeasurementUnitRateId || index}>
                                            <td>{rate.MeasurementUnitRateId}</td>
                                            <td>{rate.Category}</td>
                                            <td>{rate.MeasurementUnitName}</td>
                                            <td>{rate.QuantityTypeName}</td>
                                            <td>{rate.Co2Value !== undefined ? rate.Co2Value : "0.00000"}</td>
                                            <td>{rate.BiogenicValue !== undefined ? rate.BiogenicValue : "0.00000"}</td>
                                            <td>{rate.WeightValue !== undefined ? rate.WeightValue : "0.00000"}</td>
                                            <td>
                                                <span style={{ fontStyle: !rate.ExternalRefType ? 'italic' : 'normal', color: !rate.ExternalRefType ? '#999' : 'inherit' }}>
                                                    {rate.ExternalRefType || '-'}
                                                </span>
                                            </td>
                                            <td>
                                                <span style={{ fontStyle: !rate.ExternalRefId ? 'italic' : 'normal', color: !rate.ExternalRefId ? '#999' : 'inherit' }}>
                                                    {rate.ExternalRefId || '-'}
                                                </span>
                                            </td>

                                            <td>
                                                <div className="ec2-actions">
                                                    <button
                                                        className="ec2-btn ec2-btn--edit"
                                                        onClick={() => handleEdit(rate.MeasurementUnitRateId)}
                                                    >
                                                        ✏️ Edit
                                                    </button>
                                                    <button
                                                        className="ec2-btn ec2-btn--delete"
                                                        onClick={() => handleDelete(rate.MeasurementUnitRateId)}
                                                    >
                                                        🗑️ Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* ✅ NEW: PAGINATION CONTROLS (BOTTOM) */}
                            {totalPages > 1 && (
                                <div className="pagination-controls" style={{
                                    marginTop: '20px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '8px',
                                    flexWrap: 'wrap'
                                }}>
                                    {/* First Page Button */}
                                    <button
                                        onClick={() => goToPage(1)}
                                        disabled={currentPage === 1}
                                        style={{
                                            padding: '8px 12px',
                                            border: '1px solid #ccc',
                                            background: currentPage === 1 ? '#e9ecef' : 'white',
                                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                        title="First Page"
                                    >
                                        ⏮ First
                                    </button>

                                    {/* Previous Button */}
                                    <button
                                        onClick={() => goToPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        style={{
                                            padding: '8px 12px',
                                            border: '1px solid #ccc',
                                            background: currentPage === 1 ? '#e9ecef' : 'white',
                                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                    >
                                        ← Previous
                                    </button>

                                    {/* Page Numbers */}
                                    {getPageNumbers().map((pageNum, index) => (
                                        pageNum === '...' ? (
                                            <span key={`ellipsis-${index}`} style={{ padding: '8px 4px' }}>...</span>
                                        ) : (
                                            <button
                                                key={pageNum}
                                                onClick={() => goToPage(pageNum)}
                                                style={{
                                                    padding: '8px 12px',
                                                    border: '1px solid #ccc',
                                                    background: currentPage === pageNum ? '#007bff' : 'white',
                                                    color: currentPage === pageNum ? 'white' : '#333',
                                                    cursor: 'pointer',
                                                    borderRadius: '4px',
                                                    fontSize: '14px',
                                                    fontWeight: currentPage === pageNum ? 'bold' : 'normal'
                                                }}
                                            >
                                                {pageNum}
                                            </button>
                                        )
                                    ))}

                                    {/* Next Button */}
                                    <button
                                        onClick={() => goToPage(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        style={{
                                            padding: '8px 12px',
                                            border: '1px solid #ccc',
                                            background: currentPage === totalPages ? '#e9ecef' : 'white',
                                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                    >
                                        Next →
                                    </button>

                                    {/* Last Page Button */}
                                    <button
                                        onClick={() => goToPage(totalPages)}
                                        disabled={currentPage === totalPages}
                                        style={{
                                            padding: '8px 12px',
                                            border: '1px solid #ccc',
                                            background: currentPage === totalPages ? '#e9ecef' : 'white',
                                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                        title="Last Page"
                                    >
                                        Last ⏭
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* ✅ EDIT MATERIAL MODAL */}
            <EditMaterialModal
                isOpen={editIndex !== null}
                onClose={() => setEditIndex(null)}
                onSave={handleSave}
                editForm={editForm}
                onChange={handleInputChange}
                epdCategories={epdCategories}
                quantityTypes={quantityTypes}
                regionName={regionName}
            />

            {/* ✅ CONFIRM MODAL */}
            <ConfirmModal
                isOpen={confirmModal.open}
                onClose={() => setConfirmModal({ ...confirmModal, open: false })}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                icon={confirmModal.icon}
                confirmText="OK"
                cancelText="Cancel"
            />

            {/* ✅ SUCCESS MODAL */}
            <SuccessModal
                isOpen={successModal.open}
                onClose={() => setSuccessModal({ ...successModal, open: false })}
                title={successModal.title}
                message={successModal.message}
                icon={successModal.icon}
            />

            {/* ✅ ERROR MODAL */}
            <ErrorModal
                isOpen={errorModal.open}
                onClose={() => setErrorModal({ ...errorModal, open: false })}
                title={errorModal.title}
                message={errorModal.message}
                icon={errorModal.icon}
            />
        </div>
    );
}

export default EditCo2WeightPage;